import {
  Content,
  Group,
  Icon,
  Item,
  ItemIndicator,
  ItemText,
  Label,
  Portal,
  Root,
  ScrollDownButton,
  ScrollUpButton,
  Separator,
  Trigger,
  Value,
  Viewport,
} from '@radix-ui/react-select'
import { Check, ChevronDown, ChevronUp } from 'lucide-react'
import { type ComponentPropsWithoutRef, type ElementRef, type RefObject } from 'react'

import { cn } from '@/lib/utils'

const Select = Root

const SelectGroup = Group

const SelectValue = Value

const SelectTrigger = ({
  className,
  children,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Trigger> & { ref?: RefObject<ElementRef<typeof Trigger> | null> }) => (
  <Trigger
    className={cn(
      'flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1',
      className
    )}
    ref={ref}
    {...props}
  >
    {children}
    <Icon asChild>
      <ChevronDown className="h-4 w-4 opacity-50" />
    </Icon>
  </Trigger>
)
SelectTrigger.displayName = Trigger.displayName

const SelectScrollUpButton = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof ScrollUpButton> & { ref?: RefObject<ElementRef<typeof ScrollUpButton> | null> }) => (
  <ScrollUpButton
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    ref={ref}
    {...props}
  >
    <ChevronUp className="h-4 w-4" />
  </ScrollUpButton>
)
SelectScrollUpButton.displayName = ScrollUpButton.displayName

const SelectScrollDownButton = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof ScrollDownButton> & {
  ref?: RefObject<ElementRef<typeof ScrollDownButton> | null>
}) => (
  <ScrollDownButton
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    ref={ref}
    {...props}
  >
    <ChevronDown className="h-4 w-4" />
  </ScrollDownButton>
)
SelectScrollDownButton.displayName = ScrollDownButton.displayName

const SelectContent = ({
  className,
  children,
  position = 'popper',
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Content> & { ref?: RefObject<ElementRef<typeof Content> | null> }) => (
  <Portal>
    <Content
      className={cn(
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=closed]:animate-out data-[state=open]:animate-in',
        position === 'popper' &&
          'data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=bottom]:translate-y-1 data-[side=top]:-translate-y-1',
        className
      )}
      position={position}
      ref={ref}
      {...props}
    >
      <SelectScrollUpButton />
      <Viewport
        className={cn(
          'p-1',
          position === 'popper' &&
            'h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]'
        )}
      >
        {children}
      </Viewport>
      <SelectScrollDownButton />
    </Content>
  </Portal>
)
SelectContent.displayName = Content.displayName

const SelectLabel = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Label> & { ref?: RefObject<ElementRef<typeof Label> | null> }) => (
  <Label className={cn('py-1.5 pr-2 pl-8 font-semibold text-sm', className)} ref={ref} {...props} />
)
SelectLabel.displayName = Label.displayName

const SelectItem = ({
  className,
  children,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Item> & { ref?: RefObject<ElementRef<typeof Item> | null> }) => (
  <Item
    className={cn(
      'relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pr-2 pl-8 text-sm outline-hidden focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className
    )}
    ref={ref}
    {...props}
  >
    <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
      <ItemIndicator>
        <Check className="h-4 w-4" />
      </ItemIndicator>
    </span>

    <ItemText>{children}</ItemText>
  </Item>
)
SelectItem.displayName = Item.displayName

const SelectSeparator = ({
  className,
  ref,
  ...props
}: ComponentPropsWithoutRef<typeof Separator> & { ref?: RefObject<ElementRef<typeof Separator> | null> }) => (
  <Separator className={cn('-mx-1 my-1 h-px bg-muted', className)} ref={ref} {...props} />
)
SelectSeparator.displayName = Separator.displayName

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
}
